import { createContext, FC, memo, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getShortcutKeysMap } from './ShortcutsKeymap'
import { KeyCodeToKey, ShortcutsCategories, ShortcutType, SpecKeys } from './Shortcuts.types'

interface ShortcutsContextProps {
    registerCategory: (category: ShortcutsCategories, shortcuts: ShortcutType) => void
    unregisterCategory: (category: ShortcutsCategories, shortcutKeys?: string[]) => void
}

const ShortcutsContext = createContext<ShortcutsContextProps | undefined>(undefined)

export const useShortcutsContext = (): ShortcutsContextProps => {
    const context = useContext(ShortcutsContext)
    if (!context) {
        throw new Error('useShortcuts must be used within a ShortcutsProvider')
    }
    return context
}

const NON_TYPING_INPUT_TYPES = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'image'])

const isTypingTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false
    const editable = target.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""]')
    if (!(editable instanceof HTMLElement)) return false
    if (editable.tagName === 'INPUT') {
        const type = (editable as HTMLInputElement).type?.toLowerCase()
        if (type && NON_TYPING_INPUT_TYPES.has(type)) return false
        return !(editable as HTMLInputElement).disabled
    }
    if (editable.tagName === 'TEXTAREA' || editable.tagName === 'SELECT') {
        return !(editable as HTMLTextAreaElement | HTMLSelectElement).disabled
    }
    return true
}

const isVisibleOpenDialog = (): boolean => {
    const dialogs = document.querySelectorAll('[role="dialog"]')
    for (const dialog of dialogs) {
        if (!(dialog instanceof HTMLElement)) continue
        if (dialog.getAttribute('aria-hidden') === 'true') continue
        if (dialog.closest('[aria-hidden="true"]')) continue
        const style = window.getComputedStyle(dialog)
        if (style.display === 'none' || style.visibility === 'hidden') continue
        const rect = dialog.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) return true
    }
    return false
}

const getShortcutKey = (event: KeyboardEvent): string => {
    let keys = []
    if (event.altKey) keys.push('Alt')
    if (event.ctrlKey) keys.push('Control')
    if (event.metaKey) keys.push('Meta')
    if (event.shiftKey) keys.push('Shift')
    keys.push(KeyCodeToKey[event.code])
    return keys.join('+')
}

const sortRegisteringKey = (key: string): string => {
    const keys = key.split('+')
    const result: string[] = []
    if (keys.includes(SpecKeys.ALT)) {
        result.push(SpecKeys.ALT)
    }
    if (keys.includes(SpecKeys.CONTROL)) {
        result.push(SpecKeys.CONTROL)
    }
    if (keys.includes(SpecKeys.META)) {
        result.push(SpecKeys.META)
    }
    if (keys.includes(SpecKeys.SHIFT)) {
        result.push(SpecKeys.SHIFT)
    }
    result.push(keys[keys.length - 1])
    return result.join('+')
}

const ShortcutsProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [shortcuts, setShortcuts] = useState<Record<string, Record<string, ((event: KeyboardEvent) => void)[]>>>({})
    const shortcutsRef = useRef(shortcuts)
    shortcutsRef.current = shortcuts

    const registerCategory = useCallback((category: ShortcutsCategories, newShortcuts: ShortcutType) => {
        setShortcuts(prevShortcuts => {
            const existingCategory = prevShortcuts[category] || {}
            const updatedCategory = { ...existingCategory }

            Object.keys(newShortcuts).forEach((key: string) => {
                const actualKey = getShortcutKeysMap()[category]?.[key]
                if (actualKey) {
                    const sortedKey = sortRegisteringKey(actualKey)
                    const callback = newShortcuts[key]
                    if (updatedCategory[sortedKey]) {
                        if (!updatedCategory[sortedKey].includes(callback)) {
                            updatedCategory[sortedKey] = [...updatedCategory[sortedKey], callback]
                        }
                    } else {
                        updatedCategory[sortedKey] = [callback]
                    }
                }
            })

            return {
                ...prevShortcuts,
                [category]: updatedCategory
            }
        })
    }, [])

    const unregisterCategory = useCallback((category: ShortcutsCategories, shortcutKeys?: string[]) => {
        setShortcuts(prevShortcuts => {
            if (!category || !(category in prevShortcuts)) {
                return prevShortcuts
            }

            if (!shortcutKeys?.length) {
                const { [category]: _, ...rest } = prevShortcuts
                return rest
            }

            const keyMap = getShortcutKeysMap()[category] || {}
            const sortedKeysToRemove = new Set(
                shortcutKeys
                    .map((key) => keyMap[key])
                    .filter(Boolean)
                    .map((key) => sortRegisteringKey(key))
            )

            const updatedCategory = { ...prevShortcuts[category] }
            sortedKeysToRemove.forEach((sortedKey) => {
                delete updatedCategory[sortedKey]
            })

            if (Object.keys(updatedCategory).length === 0) {
                const { [category]: _, ...rest } = prevShortcuts
                return rest
            }

            return {
                ...prevShortcuts,
                [category]: updatedCategory
            }
        })
    }, [])

    const handleKeydown = useCallback((event: KeyboardEvent) => {
        if (isTypingTarget(event.target) || isVisibleOpenDialog()) return

        const key = getShortcutKey(event)
        const currentShortcuts = shortcutsRef.current
        for (const category in currentShortcuts) {
            const callbackArr = currentShortcuts[category][key]
            if (callbackArr && callbackArr.length > 0) {
                callbackArr.forEach(callback => {
                    callback(event)
                })
                break
            }
        }
    }, [])

    useEffect(() => {
        window.addEventListener('keydown', handleKeydown)
        return () => {
            window.removeEventListener('keydown', handleKeydown)
        }
    }, [handleKeydown])

    const contextValue = useMemo(() => ({
        registerCategory,
        unregisterCategory
    }), [registerCategory, unregisterCategory])

    return (
        <ShortcutsContext.Provider value={contextValue}>
            {children}
        </ShortcutsContext.Provider>
    )
}

export default memo(ShortcutsProvider)
